import { signal } from "@preact/signals-react";

import { markActivityPulse } from "@/shared/utils/activity";
import { loadHealth, reloadAppDataResources, type AppDataResource } from "@/app/api/app-api";
import { backendVersionDiffersFromFrontend } from "@/shared/utils/build-info";
import { hasConfiguredBackend } from "@/shared/api/backend";
import { clearSession, sessionUser } from "@/pages/authentication/state/session";
import { hasUnsavedChanges, requestUpdateReload } from "@/shared/utils/update-reload";
import { resolveWebSocketURL } from "@/shared/api/websocket";

export type SyncEnvelope = {
    type?: string;
    data?: unknown;
};

export type SyncLogLevel = "info" | "success" | "warning" | "error";

export type SyncLogEntry = {
    id: string;
    ts: string;
    level: SyncLogLevel;
    message: string;
};

export type SyncCloseKind = "administrator_kick" | "session_expired" | "transient";
export type SyncSessionProbe = () => Promise<"valid" | "invalid" | "unavailable">;

export type SyncRuntime = {
    currentURL: string | null;
    activeURL: string | null;
    handshakeStatus: string;
    lastCloseCode: number | null;
    lastCloseReason: string | null;
    lastErrorAt: string | null;
    reconnectAttempt: number;
    reconnectDelayMS: number | null;
    reconnectETA: string | null;
    lastFailureNote: string | null;
    queuedCount: number;
};

let socket: WebSocket | null = null;
let reconnectTimer: number | null = null;
let reloadTimer: number | null = null;
let reloadInFlight = false;
let upgradeReloadPending = false;
let recoveryReloadPending = false;
let sessionProbeInFlight: Promise<"valid" | "invalid" | "unavailable"> | null = null;
const openHandlers = new Set<() => void>();
const closeHandlers = new Set<(kind: SyncCloseKind) => void>();
const messageHandlers = new Set<(message: SyncEnvelope) => void>();
const suppressedReloadTypes = new Set<string>();
const pendingReloadTypes = new Set<string>();
const pendingReloadResources = new Set<AppDataResource>();

export const syncConnectionState = signal<"idle" | "connecting" | "connected" | "disconnected">("idle");
export const syncLastEventType = signal("");
export const syncLogs = signal<SyncLogEntry[]>([]);
export const syncRuntime = signal<SyncRuntime>({
    currentURL: null,
    activeURL: null,
    handshakeStatus: "idle",
    lastCloseCode: null,
    lastCloseReason: null,
    lastErrorAt: null,
    reconnectAttempt: 0,
    reconnectDelayMS: null,
    reconnectETA: null,
    lastFailureNote: null,
    queuedCount: 0,
});

export function getReloadResourcesForSyncTypes(types: readonly string[], isHAP = sessionUser.value?.role === "HAP") {
    if (isHAP) {
        return types.includes("hap.updated") ? ["hap"] as AppDataResource[] : [];
    }

    const resources = new Set<AppDataResource>();
    for (const type of types) {
        if (type === "beds.updated") resources.add("beds");
        if (type === "patients.updated") resources.add("patients");
        if (type === "ovd.updated") resources.add("ovds");
        if (type === "hap.updated") resources.add("hap");
    }
    return [...resources];
}

export function getReconnectResources(isHAP = sessionUser.value?.role === "HAP"): AppDataResource[] {
    return isHAP ? ["hap"] : ["beds", "patients", "ovds", "hap", "users"];
}

export function classifySyncClose(event: Pick<CloseEvent, "code" | "reason">): SyncCloseKind {
    if (event.code === 4001 || event.reason === "kicked") {
        return "administrator_kick";
    }
    if (event.code === 4002 || event.reason === "session expired") {
        return "session_expired";
    }
    return "transient";
}

export function startSync() {
    if (!hasConfiguredBackend() || !sessionUser.value || socket || syncConnectionState.value === "connecting") {
        return;
    }

    const url = resolveSyncWebSocketURL();
    if (!url) {
        return;
    }

    syncConnectionState.value = "connecting";
    syncRuntime.value = {
        ...syncRuntime.value,
        currentURL: url,
        activeURL: null,
        handshakeStatus: "connecting",
        lastFailureNote: null,
    };
    appendSyncLog("info", `connecting ${url}`);
    const nextSocket = new WebSocket(url);
    socket = nextSocket;
    appendSyncLog("info", `ws connect ${url}`);

    nextSocket.addEventListener("open", () => {
        if (socket !== nextSocket) {
            return;
        }
        syncConnectionState.value = "connected";
        const recovering = recoveryReloadPending;
        recoveryReloadPending = false;
        syncRuntime.value = {
            ...syncRuntime.value,
            activeURL: url,
            handshakeStatus: "open",
            reconnectAttempt: 0,
            reconnectDelayMS: null,
            reconnectETA: null,
            lastFailureNote: null,
        };
        appendSyncLog("success", `open ${url}`);
        for (const handler of openHandlers) {
            handler();
        }
        if (recovering) {
            scheduleResourceReload(getReconnectResources(), "reconnect");
        }
        if (upgradeReloadPending) {
            void checkForBackendUpgrade();
        }
    });

    nextSocket.addEventListener("message", (event) => {
        if (socket !== nextSocket) {
            return;
        }
        try {
            const payload = JSON.parse(String(event.data)) as SyncEnvelope;
            const type = String(payload.type ?? "").trim();
            markActivityPulse();
            syncLastEventType.value = type;
            appendSyncLog("info", type ? `rx ${type}` : "rx message");
            if (["beds.updated", "patients.updated", "ovd.updated", "hap.updated"].includes(type) && !suppressedReloadTypes.has(type)) {
                appendSyncLog("success", `schedule reload after ${type}`);
                scheduleReload(type);
            }
            for (const handler of messageHandlers) {
                handler(payload);
            }
        } catch {
            appendSyncLog("warning", "rx malformed message");
        }
    });

    nextSocket.addEventListener("close", (event) => {
        if (socket === nextSocket) {
            socket = null;
            const closeKind = classifySyncClose(event);
            for (const handler of closeHandlers) {
                handler(closeKind);
            }
            if (closeKind === "administrator_kick" || closeKind === "session_expired") {
                syncConnectionState.value = "idle";
                syncRuntime.value = {
                    ...syncRuntime.value,
                    activeURL: null,
                    handshakeStatus: "closed",
                    lastCloseCode: event.code,
                    lastCloseReason: event.reason || null,
                    lastFailureNote: event.reason || "connection closed",
                };
                appendSyncLog("error", `close code=${event.code} reason=${event.reason || "-"}`);
                clearSession();
                return;
            }
            syncConnectionState.value = sessionUser.value ? "disconnected" : "idle";
            recoveryReloadPending = true;
            upgradeReloadPending = true;
            syncRuntime.value = {
                ...syncRuntime.value,
                activeURL: null,
                handshakeStatus: "closed",
                lastCloseCode: event.code,
                lastCloseReason: event.reason || null,
                lastFailureNote: event.reason || (event.wasClean ? "closed" : "connection lost"),
            };
            appendSyncLog(event.wasClean ? "warning" : "error", `close code=${event.code} reason=${event.reason || "-"}`);
            probeSessionAfterDisconnect();
            scheduleReconnect();
        }
    });

    nextSocket.addEventListener("error", () => {
        if (socket !== nextSocket) {
            return;
        }
        const ts = new Date().toISOString();
        syncRuntime.value = {
            ...syncRuntime.value,
            lastErrorAt: ts,
            handshakeStatus: "error",
            lastFailureNote: "websocket error",
        };
        appendSyncLog("error", "socket error");
    });
}

export function stopSync() {
    if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    if (reloadTimer !== null) {
        window.clearTimeout(reloadTimer);
        reloadTimer = null;
    }
    pendingReloadTypes.clear();
    pendingReloadResources.clear();
    recoveryReloadPending = false;
    sessionProbeInFlight = null;
    if (socket) {
        const currentSocket = socket;
        socket = null;
        currentSocket.close();
    }
    upgradeReloadPending = false;
    syncConnectionState.value = "idle";
    syncLastEventType.value = "";
    syncRuntime.value = {
        ...syncRuntime.value,
        activeURL: null,
        handshakeStatus: "idle",
        reconnectDelayMS: null,
        reconnectETA: null,
    };
    appendSyncLog("info", "sync stopped");
}

export function clearSyncLogs() {
    syncLogs.value = [];
}

export function onSyncOpen(handler: () => void) {
    openHandlers.add(handler);
    return () => {
        openHandlers.delete(handler);
    };
}

export function onSyncClose(handler: (kind: SyncCloseKind) => void) {
    closeHandlers.add(handler);
    return () => {
        closeHandlers.delete(handler);
    };
}

export function setSyncSessionProbe(probe: SyncSessionProbe | null) {
    sessionProbe = probe;
}

export function onSyncMessage(handler: (message: SyncEnvelope) => void) {
    messageHandlers.add(handler);
    return () => {
        messageHandlers.delete(handler);
    };
}

export function suppressSyncReload(type: string) {
    suppressedReloadTypes.add(type);
    return () => {
        suppressedReloadTypes.delete(type);
    };
}

export function publishSyncMessage(type: string, data?: unknown) {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        return false;
    }

    markActivityPulse();
    socket.send(JSON.stringify({ type, data }));
    return true;
}

function scheduleReconnect() {
    if (reconnectTimer !== null || !sessionUser.value) {
        return;
    }
    const delay = 1500;
    const reconnectAttempt = syncRuntime.value.reconnectAttempt + 1;
    const reconnectETA = new Date(Date.now() + delay).toISOString();
    syncRuntime.value = {
        ...syncRuntime.value,
        reconnectAttempt,
        reconnectDelayMS: delay,
        reconnectETA,
    };
    appendSyncLog("warning", `reconnect in ${delay}ms (attempt ${reconnectAttempt})`);
    reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        startSync();
    }, delay);
}

function scheduleReload(type: string) {
    pendingReloadTypes.add(type);
    for (const resource of getReloadResourcesForSyncTypes([type])) {
        pendingReloadResources.add(resource);
    }
    if (reloadTimer !== null) {
        window.clearTimeout(reloadTimer);
    }
    reloadTimer = window.setTimeout(flushScheduledReloads, 150);
}

function scheduleResourceReload(resources: readonly AppDataResource[], reason: string) {
    for (const resource of resources) {
        pendingReloadResources.add(resource);
    }
    if (reloadTimer !== null) {
        window.clearTimeout(reloadTimer);
    }
    appendSyncLog("success", `schedule reload after ${reason}`);
    reloadTimer = window.setTimeout(flushScheduledReloads, 150);
}

function flushScheduledReloads() {
    reloadTimer = null;
    if (reloadInFlight) {
        return;
    }

    const types = [...pendingReloadTypes];
    const resources = [...new Set([...pendingReloadResources, ...getReloadResourcesForSyncTypes(types)])];
    if (resources.length === 0) {
        pendingReloadTypes.clear();
        pendingReloadResources.clear();
        return;
    }

    if (hasUnsavedChanges.value) {
        appendSyncLog("warning", `deferred reload after ${types.join(", ") || "reconnect"} because unsaved changes exist`);
        return;
    }

    pendingReloadTypes.clear();
    pendingReloadResources.clear();

    reloadInFlight = true;
    void reloadAppDataResources(resources)
        .then(() => appendSyncLog("success", `reloaded ${resources.join(", ")} after ${types.join(", ")}`))
        .catch((error) => appendSyncLog("error", error instanceof Error ? `reload failed after ${types.join(", ")}: ${error.message}` : `reload failed after ${types.join(", ")}`))
        .finally(() => {
            reloadInFlight = false;
            if ((pendingReloadTypes.size > 0 || pendingReloadResources.size > 0) && reloadTimer === null) {
                reloadTimer = window.setTimeout(flushScheduledReloads, 0);
            }
        });
}

let sessionProbe: SyncSessionProbe | null = null;

function probeSessionAfterDisconnect() {
    if (!sessionProbe || sessionProbeInFlight || !sessionUser.value) {
        return;
    }

    const userID = sessionUser.value.id;
    sessionProbeInFlight = sessionProbe();
    void sessionProbeInFlight
        .then((outcome) => {
            if (sessionUser.value?.id !== userID) {
                return;
            }
            if (outcome === "invalid") {
                clearSession();
            }
        })
        .catch(() => {
            // Probe failures remain transient and must not clear session state.
        })
        .finally(() => {
            sessionProbeInFlight = null;
        });
}

async function checkForBackendUpgrade() {
    if (!upgradeReloadPending) {
        return;
    }

    try {
        const health = await loadHealth();
        if (backendVersionDiffersFromFrontend(health)) {
            appendSyncLog("warning", `backend version changed to ${health.version ?? "unknown"}, reloading page`);
            upgradeReloadPending = false;
            requestUpdateReload();
            return;
        }
        appendSyncLog("info", "backend version matches frontend after reconnect");
    } catch (error) {
        appendSyncLog("warning", error instanceof Error ? `health check after reconnect failed: ${error.message}` : "health check after reconnect failed");
    } finally {
        upgradeReloadPending = false;
    }
}

export function resolveSyncWebSocketURL() {
    return resolveWebSocketURL();
}

function appendSyncLog(level: SyncLogLevel, message: string) {
    const entry = {
        id: createClientID(),
        ts: new Date().toISOString(),
        level,
        message,
    } satisfies SyncLogEntry;

    syncLogs.value = [...syncLogs.value, entry].slice(-600);
}

function createClientID() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    return `sync-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
